/**
 * Floating button plus a scrollable panel showing every bridge message. The
 * data comes from `MessageLog`; this file is only presentation.
 */

import type { BridgeLogEntry } from '@narrative.io/app-bridge/protocol'
import { el } from '../vendor/dom.js'
import { formatMessage, formatTime, type MessageLog } from './log.js'

/**
 * A `<span>` whose content is set via `textContent`, so untrusted values render
 * as text. Lives here rather than in the vendored `dom.ts`: the log overlay is
 * this repository's addition, and the reference example should not grow exports
 * to serve one consumer.
 */
function text(className: string, value: string): HTMLElement {
	const span = document.createElement('span')
	span.className = className
	span.textContent = value
	return span
}

function renderRow(entry: BridgeLogEntry): HTMLElement {
	const row = el('<div class="log-row"></div>')
	row.append(
		text('t', formatTime(entry.at)),
		text(`dir-${entry.direction}`, entry.direction === 'sent' ? '→' : '←'),
		text('t', entry.transport),
		// `label` and `message` originate on the other side of the boundary, so
		// both go in as text rather than markup.
		text('label', entry.label),
		text('payload', formatMessage(entry.message)),
	)
	return row
}

export function mountLogOverlay(log: MessageLog): void {
	const button = el('<button class="log-fab" type="button"></button>')
	const count = text('log-count', String(log.size))
	button.append(document.createTextNode('Bridge log ('), count, document.createTextNode(')'))

	log.onAppend(() => {
		count.textContent = String(log.size)
	})

	button.addEventListener('click', () => {
		const overlay = el(`<div class="log-overlay">
			<div class="log-panel">
				<div class="log-head">
					<h2>Bridge messages — everything crossing the postMessage boundary</h2>
					<button id="log-clear" type="button">Clear</button>
					<button id="log-close" type="button">Close</button>
				</div>
				<div class="log-body"></div>
			</div>
		</div>`)
		const body = overlay.querySelector('.log-body') as HTMLElement

		const scrollToEnd = () => {
			body.scrollTop = body.scrollHeight
		}
		const repaint = () => {
			body.replaceChildren(...log.snapshot().map(renderRow))
			scrollToEnd()
		}
		repaint()

		// While the panel is open, append live instead of repainting everything.
		log.onAppend((entry) => {
			count.textContent = String(log.size)
			body.append(renderRow(entry))
			scrollToEnd()
		})

		const close = () => {
			log.onAppend(() => {
				count.textContent = String(log.size)
			})
			overlay.remove()
		}

		overlay.addEventListener('click', (event) => {
			if (event.target === overlay) close()
		})
		overlay.querySelector('#log-close')?.addEventListener('click', close)
		overlay.querySelector('#log-clear')?.addEventListener('click', () => {
			log.clear()
			count.textContent = '0'
			body.replaceChildren()
		})
		document.body.append(overlay)
	})

	document.body.append(button)
}
